
import { User } from "@supabase/supabase-js";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

// Import the logo images
import logoImage from "/lovable-uploads/92e0f3f2-60cf-4cae-a3eb-2d4f4bfba8c9.png";
import ingantLogo from "/lovable-uploads/25a60cad-7663-40f4-8897-39f385f54f39.png";

const steps = ["Define requirements", "Edit organization structure", "Define roles", "Find candidates", "Summary"];

interface TopFrameProps {
  user: User | null;
  hideProgress?: boolean;
  moduleName?: string;
}

export const TopFrame = ({
  user,
  hideProgress = false,
  moduleName
}: TopFrameProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const progressValue = currentStep / steps.length * 100;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isUnitHead, setIsUnitHead] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [isPasswordChangeForced, setIsPasswordChangeForced] = useState(false);
  const location = useLocation();
  
  const getModuleLabel = () => {
    // If moduleName is provided, use it directly
    if (moduleName) {
      return moduleName;
    }

    // Otherwise use the existing path-based logic
    const path = location.pathname;
    if (path.includes('administration')) {
      return 'Administration module';
    } else if (path.includes('dashboard')) {
      return 'HR module - Talent Acquisition';
    } else if (path.includes('marketing')) {
      return 'Marketing module';
    } else if (path.includes('sales')) {
      return 'Sales module - Account Business Plan (ABP)';
    } else if (path.includes('legal')) {
      return 'Legal module';
    } else if (path.includes('finance')) {
      return 'Finance module - Sales Finance';
    } else if (path.includes('projects')) {
      return 'Projects module';
    } else if (path.includes('performance')) {
      return 'Performance Management module';
    }
    return '';
  };

  // Check if user is unit head and first-time user
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) return;
      
      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('is_unit_head, last_login')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error("Error checking user status:", error);
          return;
        }

        setIsUnitHead(userData?.is_unit_head || false);
        
        // Check if this is a first-time user or if localStorage indicates first-time user
        const isFirstTime = userData?.last_login === null || localStorage.getItem('isFirstTimeUser') === 'true';
        setIsFirstTimeUser(isFirstTime);
        
        if (isFirstTime) {
          setIsPasswordChangeForced(true);
          setIsOpen(true); // Auto-open the password change popover
          // Clear the localStorage flag
          localStorage.removeItem('isFirstTimeUser');
        }
      } catch (error) {
        console.error("Error checking user status:", error);
      }
    };

    checkUserStatus();
  }, [user]);
  
  useEffect(() => {
    const step = parseInt(searchParams.get('step') || '1');
    setCurrentStep(step);
  }, [searchParams]);
  
  const handleSignOut = async () => {
    try {
      const {
        data: {
          session
        },
        error: sessionError
      } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('Session check error:', sessionError);
        navigate('/');
        return;
      }
      if (!session) {
        console.log('No active session found');
        navigate('/');
        return;
      }
      const {
        error
      } = await supabase.auth.signOut({
        scope: 'local'
      });
      if (error) {
        console.error('Sign out error:', error);
        if (error.message.includes('session_not_found')) {
          navigate('/');
          return;
        }
        throw error;
      }
      navigate('/');
      toast.success('Successfully signed out');
    } catch (error) {
      console.error('Sign out error:', error);
      navigate('/');
      toast.error('Error during sign out, but you have been redirected to home');
    }
  };
  
  const handlePasswordChange = async () => {
    // For first-time users, skip current password validation
    if (!isFirstTimeUser && !currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long");
      return;
    }
    setIsLoading(true);
    try {
      const {
        data: {
          session
        },
        error: sessionError
      } = await supabase.auth.getSession();
      if (sessionError) {
        throw sessionError;
      }
      if (!session) {
        toast.error("Your session has expired. Please sign in again.");
        navigate('/');
        return;
      }
      
      // For first-time users, we don't need to verify current password
      if (!isFirstTimeUser) {
        const {
          error: signInError
        } = await supabase.auth.signInWithPassword({
          email: user?.email || '',
          password: currentPassword
        });
        if (signInError) {
          toast.error("Current password is incorrect");
          setIsLoading(false);
          return;
        }
      }
      
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      
      // If this was a first-time user, update last_login and remove forced state
      if (isFirstTimeUser) {
        try {
          await supabase.from('users').update({ 
            last_login: new Date().toISOString() 
          }).eq('id', user?.id);
          setIsFirstTimeUser(false);
          setIsPasswordChangeForced(false);
        } catch (dbError) {
          console.error('Error updating last_login:', dbError);
        }
      }
      
      toast.success("Password updated successfully");
      setIsOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error('Password change error:', error);
      toast.error(error.message || "Failed to update password");
      
      // If password change fails for first-time user, log them out
      if (isFirstTimeUser) {
        toast.error("Password change failed. You will be logged out for security.");
        setTimeout(async () => {
          await supabase.auth.signOut();
          navigate('/');
        }, 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="sticky top-0 z-50">
      <header className="border-b border-primary/20 bg-secondary/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center py-[4px]">
          <div className="flex flex-col items-start">
            <div className="flex items-center">
              <img src={logoImage} alt="HumanGood.AI Logo" className="h-[32px] w-auto mr-3" />
              <div className="flex flex-col items-start">
                <img src={ingantLogo} alt="Ingant Logo" className="h-[24px] w-auto" />
              </div>
            </div>
          </div>
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <span className="text-white font-medium">
              {getModuleLabel()}
            </span>
          </div>
          {user && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {isUnitHead && (
                  <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30">
                    Unit Head
                  </Badge>
                )}
                <Avatar className="border-2 border-primary/20">
                  <AvatarFallback className="bg-primary/10 text-white">
                    {user.email?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Popover open={isOpen} onOpenChange={(open) => {
                  // Prevent closing if password change is forced
                  if (!open && isPasswordChangeForced) {
                    return;
                  }
                  setIsOpen(open);
                }}>
                  <PopoverTrigger>
                    <span className={`text-sm cursor-pointer ${isPasswordChangeForced ? 'text-red-400 font-semibold animate-pulse' : 'text-gray-300 hover:text-white'}`}>
                      {user.email} {isPasswordChangeForced && '(Password Change Required)'}
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 bg-[#1A1F2C] border-primary/20">
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <h4 className={`font-medium leading-none text-sm ${isPasswordChangeForced ? 'text-red-400' : 'text-[#D6BCFA]'}`}>
                          {isPasswordChangeForced ? 'Required: Change Password' : 'Change Password'}
                        </h4>
                        <p className={`text-xs ${isPasswordChangeForced ? 'text-red-300' : 'text-[#D6BCFA]/80'}`}>
                          {isPasswordChangeForced 
                            ? 'You must change your password before continuing. All other functions are disabled.'
                            : 'Enter your current password and new password below.'
                          }
                        </p>
                      </div>
                      {!isFirstTimeUser && (
                        <div className="flex items-center gap-2">
                          <Label htmlFor="current-password" className="text-[#D6BCFA] text-xs w-28">Current Password</Label>
                          <Input 
                            id="current-password" 
                            type="password" 
                            value={currentPassword} 
                            onChange={e => setCurrentPassword(e.target.value)} 
                            className="bg-[#1A1F2C]/50 border-primary/20 text-[#D6BCFA] text-xs flex-1" 
                            required
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Label htmlFor="new-password" className="text-[#D6BCFA] text-xs w-28">New Password</Label>
                        <Input 
                          id="new-password" 
                          type="password" 
                          value={newPassword} 
                          onChange={e => setNewPassword(e.target.value)} 
                          className="bg-[#1A1F2C]/50 border-primary/20 text-[#D6BCFA] text-xs flex-1" 
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="confirm-password" className="text-[#D6BCFA] text-xs w-28">Confirm Password</Label>
                        <Input 
                          id="confirm-password" 
                          type="password" 
                          value={confirmPassword} 
                          onChange={e => setConfirmPassword(e.target.value)} 
                          className="bg-[#1A1F2C]/50 border-primary/20 text-[#D6BCFA] text-xs flex-1" 
                        />
                      </div>
                      <div className="flex justify-center">
                        <Button 
                          onClick={handlePasswordChange} 
                          disabled={isLoading || (!isFirstTimeUser && !currentPassword) || !newPassword || !confirmPassword} 
                          className={`text-xs h-8 px-4 ${isPasswordChangeForced ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-primary/20 hover:bg-primary/30 text-[#D6BCFA]'}`}
                          size="sm"
                        >
                          {isLoading ? "Updating..." : isPasswordChangeForced ? "Change Password Now" : "Update Password"}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Button 
                variant="outline" 
                onClick={handleSignOut} 
                disabled={isPasswordChangeForced}
                className={`${isPasswordChangeForced ? 'opacity-50 cursor-not-allowed' : 'bg-primary/10 border-primary/20 text-white hover:bg-transparent hover:text-primary'}`}
              >
                Sign out
              </Button>
            </div>
          )}
        </div>
      </header>
      {!hideProgress && (
        <div className="bg-secondary/95 backdrop-blur-lg border-b border-primary/20 p-4 py-[3px]">
          <div className="max-w-7xl mx-auto py-[4px] my-[9px]">
            <Progress value={progressValue} className="h-1.5 mb-3 bg-primary/10" />
            <div className="flex justify-between items-center text-sm">
              {steps.map((step, index) => (
                <div key={step} className="flex items-center">
                  <div className={`${index + 1 < currentStep ? "text-white font-medium" : index + 1 === currentStep ? "text-white font-medium" : "text-gray-500"}`}>
                    {step}
                  </div>
                  {index + 1 < currentStep && index + 1 < steps.length && (
                    <span className="text-[#8B5CF6] mx-2 font-bold text-purple-500 text-[15px]">&gt;</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
